import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Search,
  Filter,
  Download,
  BarChart3,
  Clock,
  CheckCircle,
  AlertTriangle,
  PlayCircle,
  Eye,
  Calendar,
  Users,
  GraduationCap,
  FileText,
  BookOpen,
  X,
  ArrowUp,
  ArrowDown,
  Target,
  Award,
  ChevronRight,
  DollarSign,
  Building2,
  UserCheck
} from 'lucide-react';

export default function CourseTracking() {
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [campusFilter, setCampusFilter] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [overallMetrics, setOverallMetrics] = useState({
    totalCourses: 0,
    totalStudents: 0,
    totalRevenue: '0.00',
    collectedRevenue: '0.00',
    averageProgress: 0,
    averageAttendance: 0
  });

  const fetchTrackingData = async () => {
    try {
      setLoading(true);

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const token = localStorage.getItem('token');

      // Fetch comprehensive tracking data from new endpoint
      const response = await fetch(
        `${API_BASE_URL}/tracking/courses/overview${campusFilter ? `?campusId=${campusFilter}` : ''}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch tracking data');

      const data = await response.json();

      setCourseData(data.courses || []);
      setCampuses(data.campuses || []);
      setOverallMetrics(data.overallMetrics || {});

    } catch (error) {
      setError(error.message);
      console.error('Error fetching tracking data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrackingData();
  }, [campusFilter]);

  const handleViewDetails = (course) => {
    navigate(`/course-details/${course.courseId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'upcoming': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'in-progress': return <PlayCircle className="h-4 w-4" />;
      case 'upcoming': return <Clock className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const filteredCourses = courseData.filter(course => {
    const matchesSearch = course.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.courseDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' ||
      (course.metrics.progress === 100 && statusFilter === 'completed') ||
      (course.metrics.progress > 0 && course.metrics.progress < 100 && statusFilter === 'in-progress') ||
      (course.metrics.progress === 0 && statusFilter === 'planning');

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <TrendingUp className="mr-3 h-8 w-8" />
              Enhanced Course Tracking
            </h1>
            <p className="mt-2 text-indigo-100">
              Comprehensive analytics across all campuses
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{overallMetrics.averageProgress}%</div>
            <div className="text-indigo-100">Average Progress</div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Courses</p>
              <p className="text-2xl font-bold text-gray-900">{overallMetrics.totalCourses}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Students</p>
              <p className="text-2xl font-bold text-purple-600">{overallMetrics.totalStudents}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <GraduationCap className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">KES {overallMetrics.totalRevenue}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Collected</p>
              <p className="text-2xl font-bold text-emerald-600">KES {overallMetrics.collectedRevenue}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Progress</p>
              <p className="text-2xl font-bold text-indigo-600">{overallMetrics.averageProgress}%</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Target className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Attendance</p>
              <p className="text-2xl font-bold text-blue-600">{overallMetrics.averageAttendance}%</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <UserCheck className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Controls Section with Campus Filter */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <select
              value={campusFilter}
              onChange={(e) => setCampusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Campuses</option>
              {campuses.map(campus => (
                <option key={campus.id} value={campus.id}>{campus.name}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="planning">Planning</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </button>
            <button className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Course Tracking Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredCourses.map((course) => (
          <div key={course.courseId} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
            {/* Course Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center flex-1">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
                  <BookOpen className="h-6 w-6" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{course.courseName}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Building2 className="h-3 w-3 text-gray-400" />
                    <p className="text-xs text-gray-500">{course.campusName}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleViewDetails(course)}
                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                title="View Details"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm font-bold text-indigo-600">{course.metrics.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${course.metrics.progress}%` }}
                ></div>
              </div>
            </div>

            {/* Statistics Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <FileText className="h-4 w-4 text-gray-600 mx-auto mb-1" />
                <div className="text-lg font-bold text-gray-900">{course.metrics.totalUnits}</div>
                <div className="text-xs text-gray-600">Units</div>
              </div>

              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <GraduationCap className="h-4 w-4 text-purple-600 mx-auto mb-1" />
                <div className="text-lg font-bold text-purple-900">{course.metrics.totalStudents}</div>
                <div className="text-xs text-purple-600">Students</div>
              </div>

              <div className="text-center p-3 bg-green-50 rounded-lg">
                <DollarSign className="h-4 w-4 text-green-600 mx-auto mb-1" />
                <div className="text-lg font-bold text-green-900">{course.metrics.collectedRevenue}</div>
                <div className="text-xs text-green-600">Collected</div>
              </div>

              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <UserCheck className="h-4 w-4 text-blue-600 mx-auto mb-1" />
                <div className="text-lg font-bold text-blue-900">{course.metrics.attendanceRate}%</div>
                <div className="text-xs text-blue-600">Attendance</div>
              </div>
            </div>

            {/* Payment Status */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-700 font-medium">Revenue Status</p>
                  <p className="text-sm font-bold text-green-900">
                    KES {course.metrics.collectedRevenue} / {course.metrics.totalRevenue}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-600">Pending</p>
                  <p className="text-sm font-semibold text-orange-600">KES {course.metrics.pendingRevenue}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredCourses.length === 0 && !loading && (
        <div className="text-center py-12">
          <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tracking data available</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || statusFilter || campusFilter
              ? 'Try adjusting your search and filter criteria.'
              : 'Course tracking data will appear here once courses and units are created.'}
          </p>
        </div>
      )}

      {/* Course Details Modal - Placeholder for now */}
      {showDetailsModal && selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold flex items-center">
                <TrendingUp className="h-6 w-6 mr-2 text-indigo-600" />
                {selectedCourse.course.name} - Detailed Analytics
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-gray-600">Detailed analytics view coming soon...</p>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
